<template>
  <div class="waiting-room-v2">
    <!-- Interface Guide Tutorial Overlay -->
    <InterfaceGuide
      ref="interfaceGuideRef"
      context="waiting-room"
      @completed="handleTutorialComplete"
      @skipped="handleTutorialSkipped"
    />

    <!-- Clean Header Bar -->
    <header class="wr2-header">
      <button class="wr2-back-btn" @click="handleBack">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>

      <div class="wr2-room-code" data-tutorial="room-code">
        <span class="wr2-label">Room</span>
        <button class="wr2-code" @click="copyRoomCode" :title="'Click to copy'">
          {{ roomCode }}
          <span class="material-symbols-outlined">content_copy</span>
        </button>
      </div>

      <div class="wr2-status">
        <div class="wr2-spinner"></div>
        <span>{{ players.length }}/4</span>
      </div>
    </header>

    <!-- Main Content -->
    <main class="wr2-main" data-tutorial="player-slots">
      <!-- Team A Section -->
      <section class="wr2-team team-a">
        <h3 class="wr2-team-name">{{ truncateTeamName(teamAName) }}</h3>
        <div class="wr2-team-players">
          <PlayerSlot
            :seat="0"
            :player="getPlayerBySeat(0)"
            :is-current-user="getPlayerBySeat(0)?.id === currentUserId"
            :is-host="isHost"
            :is-waiting="status === 'waiting'"
            :team="'A'"
            @kick="handleKickPlayer"
            @add-bot="handleAddBot"
          />
          <PlayerSlot
            :seat="2"
            :player="getPlayerBySeat(2)"
            :is-current-user="getPlayerBySeat(2)?.id === currentUserId"
            :is-host="isHost"
            :is-waiting="status === 'waiting'"
            :team="'A'"
            @kick="handleKickPlayer"
            @add-bot="handleAddBot"
          />
        </div>
      </section>

      <!-- Logo Divider -->
      <div class="wr2-divider">
        <div class="wr2-divider-line"></div>
        <img src="/images/logosmall.png" alt="Canasta" class="wr2-logo" />
        <div class="wr2-divider-line"></div>
      </div>

      <!-- Team B Section -->
      <section class="wr2-team team-b">
        <h3 class="wr2-team-name">{{ truncateTeamName(teamBName) }}</h3>
        <div class="wr2-team-players">
          <PlayerSlot
            :seat="1"
            :player="getPlayerBySeat(1)"
            :is-current-user="getPlayerBySeat(1)?.id === currentUserId"
            :is-host="isHost"
            :is-waiting="status === 'waiting'"
            :team="'B'"
            @kick="handleKickPlayer"
            @add-bot="handleAddBot"
          />
          <PlayerSlot
            :seat="3"
            :player="getPlayerBySeat(3)"
            :is-current-user="getPlayerBySeat(3)?.id === currentUserId"
            :is-host="isHost"
            :is-waiting="status === 'waiting'"
            :team="'B'"
            @kick="handleKickPlayer"
            @add-bot="handleAddBot"
          />
        </div>
      </section>
    </main>

    <!-- Footer Actions -->
    <footer class="wr2-footer">
      <button class="wr2-btn-leave" @click="handleLeaveGame">
        Leave Game
      </button>
      <button
        v-if="isHost"
        class="wr2-btn-start"
        data-tutorial="start-game"
        :disabled="!canStartGame"
        @click="handleStartGame"
      >
        <span class="material-symbols-outlined">play_arrow</span>
        Start Game
      </button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import LobbyMenu from '@/components/common/LobbyMenu.vue'
import InterfaceGuide from '@/components/tutorial/InterfaceGuide.vue'
import PlayerSlot from '@/components/lobby/PlayerSlot.vue'
import { useTutorial } from '@/composables/useTutorial'

const tutorial = useTutorial()
const interfaceGuideRef = ref<InstanceType<typeof InterfaceGuide> | null>(null)

/**
 * WaitingRoom Component
 *
 * Displays the game lobby waiting room with:
 * - Room code display and copy functionality
 * - Player list organized by teams (2x2 grid)
 * - Player avatars and names
 * - Ready/Unready states
 * - Start game button (host only)
 * - Add bot buttons (host only)
 * - Kick player buttons (host only)
 *
 * Real-time updates via WebSocket events.
 *
 * @component
 * @example
 * <WaitingRoom
 *   :room-code="roomCode"
 *   :players="players"
 *   :teams="teams"
 *   :current-user-id="currentUser.id"
 *   :creator-user-id="game.creator_user_id"
 *   :status="game.status"
 *   @start-game="handleStartGame"
 *   @add-bot="handleAddBot"
 *   @kick-player="handleKickPlayer"
 *   @leave-game="handleLeaveGame"
 *   @back="handleBack"
 * />
 */

// ============================================================================
// Props & Emits
// ============================================================================

interface Player {
  id: string
  username: string
  display_name?: string
  profile_picture?: string
  seat: number
  team: string
  is_bot: boolean
}

interface Team {
  name: string
}

interface Props {
  roomCode: string
  players: Player[]
  teams: { A: Team; B: Team }
  currentUserId: string
  creatorUserId: string
  status: string
}

interface Emits {
  (e: 'start-game'): void
  (e: 'add-bot'): void
  (e: 'kick-player', playerId: string): void
  (e: 'leave-game'): void
  (e: 'back'): void
  (e: 'logout'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// ============================================================================
// Computed Properties
// ============================================================================

/**
 * Check if current user is the host
 */
const isHost = computed(() => {
  return props.currentUserId === props.creatorUserId
})

/**
 * Check if game can be started (all 4 players present)
 */
const canStartGame = computed(() => {
  return props.players.length === 4 && props.status === 'waiting'
})

/**
 * Team A name
 */
const teamAName = computed(() => {
  return props.teams.A?.name || 'Team A'
})

/**
 * Team B name
 */
const teamBName = computed(() => {
  return props.teams.B?.name || 'Team B'
})

// ============================================================================
// Methods
// ============================================================================

/**
 * Truncate team name if too long
 */
function truncateTeamName(name: string, maxLength: number = 18): string {
  if (name.length <= maxLength) return name
  return name.substring(0, maxLength - 1) + '...'
}

/**
 * Get player by seat number
 */
function getPlayerBySeat(seat: number): Player | undefined {
  return props.players.find((p) => p.seat === seat)
}

/**
 * Get player slot CSS class
 */
function getPlayerSlotClass(seat: number): string {
  const player = getPlayerBySeat(seat)
  return player ? 'filled' : 'empty'
}

/**
 * Get player display name
 */
function getPlayerDisplayName(player: Player | undefined): string {
  if (!player) return 'Unknown'
  if (player.is_bot) return player.username
  return player.display_name || player.username || 'Player'
}

/**
 * Get player initials for avatar fallback
 */
function getPlayerInitials(player: Player | undefined): string {
  if (!player) return '?'
  const name = getPlayerDisplayName(player)
  const parts = name.split(' ')
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

/**
 * Copy room code to clipboard
 */
async function copyRoomCode() {
  try {
    await navigator.clipboard.writeText(props.roomCode)
    // Show success message (could emit event to parent)
  } catch (error) {
    console.error('Failed to copy room code:', error)
  }
}

/**
 * Handle start game button click
 */
function handleStartGame() {
  emit('start-game')
}

/**
 * Handle add bot button click
 */
function handleAddBot() {
  emit('add-bot')
}

/**
 * Handle kick player button click
 */
function handleKickPlayer(playerId: string) {
  emit('kick-player', playerId)
}

/**
 * Handle leave game button click
 */
function handleLeaveGame() {
  emit('leave-game')
}

/**
 * Handle back button click
 */
function handleBack() {
  emit('back')
}

/**
 * Handle logout from LobbyMenu
 */
function handleLogout() {
  emit('logout')
}

/**
 * Handle tutorial completion
 */
function handleTutorialComplete() {
  // Tutorial completed, continue first-time flow if active
}

/**
 * Handle tutorial skipped
 */
function handleTutorialSkipped() {
  // Tutorial was skipped
}

// Trigger waiting room tutorial when component mounts (if in first-time flow)
onMounted(() => {
  tutorial.continueFirstTimeFlow('waiting-room')
})
</script>

<style scoped>
/* ===========================================
   WAITING ROOM V2 - Stacked Teams Layout
   =========================================== */

.waiting-room-v2 {
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #0a1520 0%, #0d1f2d 100%);
  overflow: hidden;
}

/* ============ HEADER ============ */
.wr2-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.wr2-back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-secondary, #a0aec0);
  cursor: pointer;
  transition: all 0.2s ease;
}

.wr2-back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary, #fff);
}

.wr2-back-btn .material-symbols-outlined {
  font-size: 22px;
}

.wr2-room-code {
  display: flex;
  align-items: center;
  gap: 12px;
}

.wr2-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--text-muted, #718096);
}

.wr2-code {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: linear-gradient(135deg, rgba(201, 162, 39, 0.12) 0%, rgba(201, 162, 39, 0.04) 100%);
  border: 1px solid rgba(201, 162, 39, 0.25);
  border-radius: 8px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 3px;
  color: var(--accent-primary, #c9a227);
  cursor: pointer;
  transition: all 0.2s ease;
}

.wr2-code:hover {
  background: linear-gradient(135deg, rgba(201, 162, 39, 0.18) 0%, rgba(201, 162, 39, 0.08) 100%);
  border-color: rgba(201, 162, 39, 0.4);
}

.wr2-code .material-symbols-outlined {
  font-size: 16px;
  opacity: 0.6;
}

.wr2-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #a0aec0);
}

.wr2-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(201, 162, 39, 0.2);
  border-top-color: var(--accent-primary, #c9a227);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============ MAIN CONTENT ============ */
.wr2-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px 24px;
  gap: 0;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}

/* ============ TEAM SECTIONS ============ */
.wr2-team {
  padding: 12px 16px;
}

.wr2-team-name {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 2px solid;
}

.wr2-team.team-a .wr2-team-name {
  color: var(--team-1-color, #c9a227);
  border-color: rgba(201, 162, 39, 0.3);
}

.wr2-team.team-b .wr2-team-name {
  color: var(--team-2-color, #c94a4a);
  border-color: rgba(201, 74, 74, 0.3);
}

.wr2-team-players {
  display: flex;
  gap: 16px;
}

.wr2-team-players > * {
  flex: 1;
}

/* ============ LOGO DIVIDER ============ */
.wr2-divider {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
}

.wr2-divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(201, 162, 39, 0.3) 50%,
    transparent 100%
  );
}

.wr2-logo {
  width: 72px;
  height: auto;
  filter: drop-shadow(0 3px 12px rgba(201, 162, 39, 0.4));
}

/* ============ FOOTER ============ */
.wr2-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 12px 20px;
  padding-bottom: 22px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.wr2-btn-leave {
  padding: 10px 20px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: var(--text-secondary, #a0aec0);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.wr2-btn-leave:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.25);
  color: var(--text-primary, #fff);
}

.wr2-btn-start {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background: linear-gradient(135deg, #c9a227 0%, #a8871e 100%);
  border: none;
  border-radius: 8px;
  color: #0d1f2d;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(201, 162, 39, 0.3);
}

.wr2-btn-start:not(:disabled):hover {
  background: linear-gradient(135deg, #d4ad2e 0%, #c9a227 100%);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(201, 162, 39, 0.4);
}

.wr2-btn-start:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

.wr2-btn-start .material-symbols-outlined {
  font-size: 18px;
}

/* ============ RESPONSIVE ============ */
@media (max-width: 600px) {
  .wr2-header {
    padding: 12px 16px;
  }

  .wr2-room-code {
    flex-direction: column;
    gap: 4px;
  }

  .wr2-label {
    font-size: 10px;
  }

  .wr2-code {
    padding: 8px 14px;
    font-size: 16px;
    letter-spacing: 3px;
  }

  .wr2-main {
    padding: 20px 16px;
  }

  .wr2-team {
    padding: 16px;
  }

  .wr2-team-players {
    flex-direction: column;
  }

  .wr2-logo {
    width: 60px;
  }

  .wr2-footer {
    flex-direction: column;
    padding: 16px;
  }

  .wr2-btn-leave,
  .wr2-btn-start {
    width: 100%;
    justify-content: center;
  }
}

/* Material icons */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}
</style>
